// src/components/UserProfile.js
// Identity (name, employee code, email, department, roles) comes from Peerapat
// SSO and is overwritten on every login, so it is shown read-only. Only the
// settings that are genuinely local to VERP can be changed here:
// related centres (notification routing), Telegram notifications, store locations.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';

const normalizeSubDepartment = (subDept) => {
  if (!subDept) return [];
  if (typeof subDept === 'string') return subDept.split(',').map(d => d.trim()).filter(Boolean);
  if (Array.isArray(subDept)) return subDept.filter(Boolean);
  return [];
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 })
};

const UserProfile = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);

  // Related centres (sub_department) — decides which notifications this person gets
  const [subDepartmentOptions, setSubDepartmentOptions] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [savingSubDepartments, setSavingSubDepartments] = useState(false);

  // Store locations
  const [allLocations, setAllLocations] = useState([]);
  const [userLocations, setUserLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [updatingLocations, setUpdatingLocations] = useState(false);

  // Telegram notifications
  const [notificationStatus, setNotificationStatus] = useState({ enabled: false, telegram_id: null, telegram_username: null });
  const [notificationLoading, setNotificationLoading] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    fetchSubDepartmentOptions();
    fetchNotificationStatus();
    if (currentUser?.employee_id) {
      fetchUserLocations();
      fetchAllLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      // Everyone signs in through SSO now, so /sso/me is the one source of truth.
      const response = await axios.get(`${API_URL}/sso/me`);
      const user = response.data?.success ? response.data.user : currentUser;
      setUserDetails(user);
      setSubDepartments(normalizeSubDepartment(user?.sub_department));
    } catch (error) {
      console.warn('Failed to fetch fresh user data, falling back to the session user:', error);
      setUserDetails(currentUser);
      setSubDepartments(normalizeSubDepartment(currentUser?.sub_department));
    } finally {
      setLoading(false);
    }
  };

  const fetchSubDepartmentOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/departments`);
      if (response.data.success) {
        setSubDepartmentOptions(
          response.data.departments
            .map(d => d.Name_no_hierarchy)
            .filter(name => name.startsWith('S'))
            .map(name => ({ label: name, value: name }))
        );
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
    }
  };

  const handleSaveSubDepartments = async () => {
    try {
      setSavingSubDepartments(true);
      // The endpoint wants the identity fields too; send them back unchanged
      // (they come from SSO) so only sub_department actually changes.
      const response = await axios.put(`${API_URL}/auth/update-profile`, {
        email: userDetails.email,
        employee_id: userDetails.employee_id,
        department: userDetails.department,
        sub_department: subDepartments.join(',')
      });
      if (response.data.success) {
        toast.success('บันทึกศูนย์/สาขาที่เกี่ยวข้องเรียบร้อย');
        setUserDetails(prev => ({ ...prev, ...response.data.user }));
      } else {
        toast.error(response.data.message || 'เกิดข้อผิดพลาดในการบันทึก');
      }
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการบันทึก: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingSubDepartments(false);
    }
  };

  // ── Telegram notifications ────────────────────────────────────────────────
  const fetchNotificationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/status`);
      if (response.data.success) setNotificationStatus(response.data.status);
    } catch (error) {
      console.error('Error fetching notification status:', error);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      setNotificationLoading(true);
      const response = await axios.post(`${API_URL}/notifications/enable-link`);
      if (response.data.success && response.data.deepLink) {
        window.open(response.data.deepLink, '_blank', 'noopener,noreferrer');
        toast.info('เปิดบอทแจ้งเตือนแล้ว กรุณากด Start ใน Telegram เพื่อเชื่อมต่อ');

        // Poll for up to 3 minutes until linked
        const deadline = Date.now() + 3 * 60 * 1000;
        const poll = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API_URL}/notifications/status`);
            if (statusRes.data.success && statusRes.data.status.enabled) {
              clearInterval(poll);
              setNotificationStatus(statusRes.data.status);
              setNotificationLoading(false);
              toast.success('เชื่อมต่อ Telegram เรียบร้อยแล้ว!');
            } else if (Date.now() > deadline) {
              clearInterval(poll);
              setNotificationLoading(false);
            }
          } catch {
            clearInterval(poll);
            setNotificationLoading(false);
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Error generating notification link:', error);
      toast.error('ไม่สามารถสร้างลิงก์เชื่อมต่อบอทแจ้งเตือนได้');
      setNotificationLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    try {
      setNotificationLoading(true);
      const response = await axios.post(`${API_URL}/notifications/disable`);
      if (response.data.success) {
        toast.success('ปิดการแจ้งเตือนเรียบร้อย');
        await fetchNotificationStatus();
      }
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('ไม่สามารถปิดการแจ้งเตือนได้');
    } finally {
      setNotificationLoading(false);
    }
  };

  // ── Store locations ───────────────────────────────────────────────────────
  const fetchAllLocations = async () => {
    try {
      setLocationsLoading(true);
      const response = await axios.get(`${API_URL}/locations/all`);
      if (response.data.success) {
        setAllLocations(response.data.locations.map(loc => ({ value: loc.id, label: `${loc.name} (${loc.id})` })));
      }
    } catch (error) {
      console.error('Error fetching all locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchUserLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/user-locations/${currentUser.employee_id}`);
      if (response.data.success) {
        setUserLocations(response.data.locations);
        setSelectedLocations(response.data.locations.map(loc => loc.location_id));
      }
    } catch (error) {
      console.error('Error fetching user locations:', error);
    }
  };

  const handleUpdateLocations = async () => {
    if (!currentUser?.employee_id) {
      toast.error('ไม่พบรหัสพนักงาน');
      return;
    }
    try {
      setUpdatingLocations(true);
      const response = await axios.post(`${API_URL}/user-locations/${currentUser.employee_id}`, { locationIds: selectedLocations });
      if (response.data.success) {
        toast.success(`อัปเดตสถานที่เรียบร้อย (${selectedLocations.length} สถานที่)`);
        setUserLocations(response.data.locations);
      } else {
        toast.error(response.data.message || 'เกิดข้อผิดพลาดในการอัปเดตสถานที่');
      }
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานที่: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingLocations(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) return <PageLoader message="กำลังโหลดข้อมูลผู้ใช้..." />;

  if (!userDetails) {
    return (
      <div className="rs" style={{ maxWidth: 560, textAlign: 'center', padding: '48px 0' }}>
        <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: '#d52b1e' }}></i>
        <h2 className="rs-card__title" style={{ marginTop: 12 }}>ไม่พบข้อมูลผู้ใช้</h2>
        <p className="rs-help">กรุณาเข้าสู่ระบบใหม่อีกครั้ง</p>
        <button onClick={handleLogout} className="rs-btn rs-btn--primary" style={{ marginTop: 12 }}>
          <i className="bi bi-box-arrow-in-right"></i> เข้าสู่ระบบใหม่
        </button>
      </div>
    );
  }

  const fullName = [userDetails.first_name, userDetails.last_name].filter(Boolean).join(' ') || '—';
  const roles = Array.isArray(userDetails.roles) ? userDetails.roles : [];
  const savedSubDepartments = normalizeSubDepartment(userDetails.sub_department);
  const subDepartmentsChanged = savedSubDepartments.join(',') !== subDepartments.join(',');

  return (
    <div className="rs profile">
      <header className="rs-header">
        <div>
          <h1 className="rs-title">โปรไฟล์</h1>
          <p className="rs-subtitle">ข้อมูลบัญชีและการตั้งค่าส่วนตัว</p>
        </div>
        <button type="button" className="rs-btn rs-btn--ghost" onClick={handleLogout}>
          <i className="bi bi-box-arrow-right"></i> ออกจากระบบ
        </button>
      </header>

      <div className="profile__grid">
        {/* Identity — from SSO, read-only */}
        <section className="rs-card">
          <div className="rs-card__head">
            <div className="rs-card__headtext">
              <div className="rs-card__kicker">01 / ACCOUNT</div>
              <h2 className="rs-card__title">บัญชีผู้ใช้</h2>
              <p className="rs-card__desc">ข้อมูลนี้มาจาก Peerapat SSO และอัปเดตอัตโนมัติทุกครั้งที่เข้าสู่ระบบ — หากต้องแก้ไข ให้แจ้งผู้ดูแล SSO</p>
            </div>
          </div>
          <div className="profile__identity">
            <div className="profile__avatar" aria-hidden="true">
              {userDetails.photo_url
                ? <img src={userDetails.photo_url} alt="" />
                : (userDetails.first_name || 'U').charAt(0)}
            </div>
            <div>
              <div className="profile__name">{fullName}</div>
              {userDetails.username && <div className="rs-help">@{userDetails.username}</div>}
            </div>
          </div>
          <dl className="rs-facts">
            <div><dt>รหัสพนักงาน</dt><dd>{userDetails.employee_id || '—'}</dd></div>
            <div><dt>อีเมล</dt><dd>{userDetails.email || '—'}</dd></div>
            <div><dt>แผนก</dt><dd>{userDetails.department || '—'}</dd></div>
            <div>
              <dt>สิทธิ์การใช้งาน</dt>
              <dd>
                {roles.length > 0
                  ? roles.map(r => <span key={r} className="rs-chip" style={{ marginRight: 6 }}>{r}</span>)
                  : '—'}
              </dd>
            </div>
          </dl>
        </section>

        {/* Notifications: Telegram link + which centres to hear about */}
        <section className="rs-card">
          <div className="rs-card__head">
            <div className="rs-card__headtext">
              <div className="rs-card__kicker">02 / NOTIFICATIONS</div>
              <h2 className="rs-card__title">การแจ้งเตือน</h2>
              <p className="rs-card__desc">รับแจ้งเตือนผ่าน Telegram และเลือกศูนย์/สาขาที่ต้องการรับข่าว</p>
            </div>
            <span className={`rs-chip ${notificationStatus.enabled ? '' : 'rs-chip--amber'}`}>
              {notificationStatus.enabled ? 'เปิดใช้งาน' : 'ยังไม่เปิดใช้งาน'}
            </span>
          </div>

          {notificationStatus.enabled ? (
            <div className="profile__row">
              <p className="rs-help" style={{ margin: 0 }}>
                เชื่อมต่อ Telegram แล้ว: {notificationStatus.telegram_username ? `@${notificationStatus.telegram_username}` : notificationStatus.telegram_id}
              </p>
              <button className="rs-btn rs-btn--ghost rs-btn--sm" onClick={handleDisableNotifications} disabled={notificationLoading}>
                {notificationLoading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-bell-slash"></i>} ปิดการแจ้งเตือน
              </button>
            </div>
          ) : (
            <div className="profile__row">
              <p className="rs-help" style={{ margin: 0 }}>กดปุ่มเพื่อเปิดบอท จากนั้นกด Start ใน Telegram เพื่อเชื่อมต่อบัญชี</p>
              <button className="rs-btn rs-btn--primary rs-btn--sm" onClick={handleEnableNotifications} disabled={notificationLoading}>
                {notificationLoading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-bell"></i>} เปิดใช้งานการแจ้งเตือน
              </button>
            </div>
          )}

          <div className="rs-field" style={{ marginTop: 20 }}>
            <label>ศูนย์/สาขาที่เกี่ยวข้อง (พบธ.)</label>
            <Select
              isMulti
              options={subDepartmentOptions}
              value={subDepartmentOptions.filter(o => subDepartments.includes(o.value))}
              onChange={(selected) => setSubDepartments(selected ? selected.map(o => o.value) : [])}
              placeholder="เลือกศูนย์/สาขา..."
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
            <span className="rs-help">เลือกศูนย์หรือสาขาที่คุณต้องการรับการแจ้งเตือน</span>
          </div>
          {subDepartmentsChanged && (
            <div style={{ marginTop: 14 }}>
              <button className="rs-btn rs-btn--primary rs-btn--sm" onClick={handleSaveSubDepartments} disabled={savingSubDepartments}>
                {savingSubDepartments ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-lg"></i>} บันทึก
              </button>
            </div>
          )}
        </section>

        {/* Store locations — used by the Sales Order editor */}
        <section className="rs-card profile__wide">
          <div className="rs-card__head">
            <div className="rs-card__headtext">
              <div className="rs-card__kicker">03 / STORES</div>
              <h2 className="rs-card__title">Store Locations</h2>
              <p className="rs-card__desc">สถานที่ที่เลือกจะแสดงใน dropdown ของหน้าแก้ไข Sales Order</p>
            </div>
            <span className="rs-chip">{userLocations.length} สถานที่</span>
          </div>

          <div className="rs-field">
            <label>เลือก Store ที่คุณต้องการใช้งาน</label>
            <Select
              isMulti
              options={allLocations}
              value={allLocations.filter(loc => selectedLocations.includes(loc.value))}
              onChange={(selected) => setSelectedLocations(selected ? selected.map(o => o.value) : [])}
              placeholder="ค้นหาและเลือกสถานที่..."
              classNamePrefix="react-select"
              isLoading={locationsLoading}
              isSearchable
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </div>

          {selectedLocations.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button className="rs-btn rs-btn--primary rs-btn--sm" onClick={handleUpdateLocations} disabled={updatingLocations}>
                {updatingLocations ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-save"></i>} บันทึกสถานที่ ({selectedLocations.length})
              </button>
            </div>
          )}

          {userLocations.length > 0 ? (
            <div style={{ marginTop: 20 }}>
              <div className="rs-subhead rs-subhead--first">สถานที่ที่บันทึกแล้ว</div>
              <div className="profile__locations">
                {userLocations.map((location) => (
                  <div key={location.location_id} className="profile__location">
                    <strong>{location.location_name}</strong>
                    <span className="rs-help">({location.location_id})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="econ-alert" style={{ marginTop: 16 }}>
              <div className="econ-alert__line">
                <i className="bi bi-exclamation-triangle me-2"></i>
                ยังไม่ได้เลือกสถานที่ — หากไม่เลือก คุณจะไม่เห็น dropdown สถานที่ในหน้าแก้ไข Sales Order
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default UserProfile;
